/* =====================================================================
 * StorageFileList — 리스트(테이블) 뷰
 * ===================================================================== */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ExternalLink,
} from 'lucide-react';
import type { WorkspaceFile } from '@auw/workspace-utils';
import {
  formatBytes,
  formatDate,
  getExtensionLabel,
  getSourceLabel,
  truncateFileName,
} from './StorageFileCard';
import {
  ListContainer,
  Table,
  Thead,
  Th,
  Tr,
  Td,
  NameCell,
  NameText,
  CheckBox,
  SourceLinkButton,
} from './storage.styles';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type SortKey = 'name' | 'date' | 'size' | 'kind';
type SortDir = 'asc' | 'desc';

const DEFAULT_SORT: Record<SortKey, SortDir> = {
  name: 'asc',
  date: 'desc',
  size: 'asc',
  kind: 'asc',
};

interface Props {
  files: WorkspaceFile[];
  selectedIds: Set<string>;
  /* P11-5f: when `mode.storage.multiSelect` is disabled the parent omits
   * these so the row checkbox column collapses entirely. */
  onToggleSelect?: (id: string) => void;
  onSelectAll?: () => void;
  allSelected: boolean;
  onOpenFile: (file: WorkspaceFile) => void;
  onContextMenu: (e: React.MouseEvent, fileId: string) => void;
  /**
   * 파일의 "출처" 컬럼 바로가기 아이콘 클릭 핸들러.
   * 핸들러가 falsy 값(예: null)을 반환하거나 정의되지 않으면 아이콘은 비활성화된다.
   * 부모는 source_item_id / metadata.workspace_type 으로 라우트 가능 여부를 결정한다.
   */
  onOpenSource?: (file: WorkspaceFile) => void;
  /** 출처 바로가기가 가능한지 결정. 미지정 시 onOpenSource 유무로 결정. */
  canOpenSource?: (file: WorkspaceFile) => boolean;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const StorageFileList: React.FC<Props> = ({
  files,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  allSelected,
  onOpenFile,
  onContextMenu,
  onOpenSource,
  canOpenSource,
}) => {
  const { t } = useTranslation('common');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT.date);

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDir(DEFAULT_SORT[key]);
      return key;
    });
  }, []);

  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  /* sort files */
  const sortedFiles = React.useMemo(() => {
    const copy = [...files];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.original_name.localeCompare(b.original_name);
          break;
        case 'size':
          cmp = a.size_bytes - b.size_bytes;
          break;
        case 'date':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'kind':
          cmp = getExtensionLabel(a.original_name).localeCompare(getExtensionLabel(b.original_name));
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [files, sortKey, sortDir]);

  const isSourceEnabled = useCallback(
    (file: WorkspaceFile): boolean => {
      if (!onOpenSource) return false;
      if (canOpenSource) return canOpenSource(file);
      return true;
    },
    [onOpenSource, canOpenSource],
  );

  const multiSelectEnabled = Boolean(onToggleSelect && onSelectAll);

  return (
    <ListContainer>
      <Table>
        <Thead>
          <tr>
            {multiSelectEnabled && (
              <Th style={{ width: 36 }}>
                <CheckBox $checked={allSelected} onClick={onSelectAll}>
                  {allSelected && <Check size={12} />}
                </CheckBox>
              </Th>
            )}
            <Th $sortable onClick={() => handleSort('name')}>
              {t('storage.colName')} <SortIcon col="name" />
            </Th>
            <Th $sortable style={{ width: 140 }} onClick={() => handleSort('date')}>
              {t('storage.colDate')} <SortIcon col="date" />
            </Th>
            <Th $sortable style={{ width: 100 }} onClick={() => handleSort('size')}>
              {t('storage.colSize')} <SortIcon col="size" />
            </Th>
            <Th $sortable style={{ width: 120 }} onClick={() => handleSort('kind')}>
              {t('storage.colKind')} <SortIcon col="kind" />
            </Th>
            <Th style={{ width: 90 }}>
              {t('storage.colSource')}
            </Th>
          </tr>
        </Thead>
        <tbody>
          {sortedFiles.map((file) => (
            <Tr
              key={file.file_id}
              $selected={selectedIds.has(file.file_id)}
              onDoubleClick={() => onOpenFile(file)}
              onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu(e, file.file_id);
              }}
            >
              {multiSelectEnabled && (
                <Td>
                  <CheckBox
                    $checked={selectedIds.has(file.file_id)}
                    onClick={() => onToggleSelect?.(file.file_id)}
                  >
                    {selectedIds.has(file.file_id) && <Check size={12} />}
                  </CheckBox>
                </Td>
              )}
              <Td>
                <NameCell>
                  <NameText title={file.original_name}>{truncateFileName(file.original_name)}</NameText>
                </NameCell>
              </Td>
              <Td>{formatDate(file.created_at)}</Td>
              <Td>{formatBytes(file.size_bytes)}</Td>
              <Td>{getExtensionLabel(file.original_name)}</Td>
              <Td>
                {(() => {
                  const enabled = isSourceEnabled(file);
                  const label = getSourceLabel(file.source_type, t);
                  return (
                    <SourceLinkButton
                      type="button"
                      title={enabled ? `${label} \u2192 ${t('storage.openSource', { defaultValue: '\uc6d0\ubcf8 \ud398\uc774\uc9c0\ub85c \uc774\ub3d9' })}` : label}
                      aria-label={label}
                      disabled={!enabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!enabled) return;
                        onOpenSource?.(file);
                      }}
                    >
                      <ExternalLink size={14} />
                    </SourceLinkButton>
                  );
                })()}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </ListContainer>
  );
};
